import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Eye, ArrowUpDown } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { usePermissions } from '../hooks/usePermissions'
import { useTickets, useCreateTicket } from '../hooks/api/useTickets'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { IssuesEmptyState } from '../components/empty-states/IssuesEmptyState'
import { Pagination } from '../components/ui/Pagination'
import { DateFilterPopover } from '../components/ui/DateFilterPopover'
import { TicketSuccessBanner } from '../components/ui/TicketSuccessBanner'
import { CreateTicketModal, TicketFormData } from '../components/modals/CreateTicketModal'
import { FilterDropdown, FilterState } from '../components/ui/FilterDropdown'

// Helper function to format ticket data for display
const formatTicketForDisplay = (ticket: any) => ({
  id: ticket.id,
  issueId: `#${ticket.id.slice(0, 6)}`,
  submittedBy: ticket.created_by_email || 'Unknown',
  description: ticket.title,
  priority: ticket.priority,
  deviceStatus: ticket.device_status || 'Unknown',
  dateSubmitted: new Date(ticket.created_at).toLocaleString(),
  status: ticket.status
})

const DATE_FILTER_TO_DAYS: Record<string, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  all: null
}

export const ManagerIssuesPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { hasPermission } = usePermissions({ userRole: userRecord?.role || null })
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState<'priority' | 'date' | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [dateFilter, setDateFilter] = useState('7d')
  const [filters, setFilters] = useState<FilterState>({
    organization: null,
    status: null,
    priority: null
  })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showSuccessBanner, setShowSuccessBanner] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const itemsPerPage = 10
  const createTicketMutation = useCreateTicket()

  // Get ALL company tickets - MANAGER VIEW (admin with null company_id gets ALL tickets)
  const { data: tickets = [], isLoading: ticketsLoading } = useTickets(userRecord?.company_id || undefined)

  useEffect(() => {
    if (!showSuccessBanner) return
    const timeout = setTimeout(() => setShowSuccessBanner(false), 5000)
    return () => clearTimeout(timeout)
  }, [showSuccessBanner])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters, dateFilter])

  const handleCreateTicket = async (ticketData: TicketFormData) => {
    if (!userRecord?.company_id || !user?.id) {
      setCreateError('Missing account information needed to submit a ticket.')
      return
    }

    setCreateError(null)

    try {
      await createTicketMutation.mutateAsync({
        ticketData: {
          title: ticketData.subject.trim(),
          description: ticketData.description,
          priority: ticketData.priority
        },
        companyId: userRecord.company_id,
        createdByUserId: user.id
      })

      setShowCreateModal(false)
      setShowSuccessBanner(true)
    } catch (createError) {
      console.error('ManagerIssuesPage: failed to create ticket', createError)
      setCreateError('Unable to create ticket. Please try again.')
    }
  }

  const handleIssueClick = (issueId: string) => {
    navigate(`/issues/${issueId}`)
  }

  const handleSort = (column: 'priority' | 'date') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
  }

  const filteredTickets = useMemo(() => {
    const now = new Date()
    const days = DATE_FILTER_TO_DAYS[dateFilter]

    return tickets.filter(ticket => {
      if (filters.status && ticket.status !== filters.status) return false
      if (filters.priority && ticket.priority !== filters.priority) return false

      if (days) {
        const threshold = new Date(now)
        threshold.setDate(now.getDate() - days)
        const createdAt = new Date(ticket.created_at)
        if (createdAt < threshold) return false
      }

      return true
    })
  }, [tickets, filters, dateFilter])

  // Sorting logic
  const sortedTickets = useMemo(() => {
    const sorted = [...filteredTickets]

    if (!sortBy) return sorted

    if (sortBy === 'priority') {
      const priorityOrder = { high: 3, medium: 2, low: 1, urgent: 4 }
      return sorted.sort((a, b) => {
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] || 0
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] || 0
        return sortOrder === 'asc' ? aPriority - bPriority : bPriority - aPriority
      })
    }

    if (sortBy === 'date') {
      return sorted.sort((a, b) => {
        const aDate = new Date(a.created_at).getTime()
        const bDate = new Date(b.created_at).getTime()
        return sortOrder === 'asc' ? aDate - bDate : bDate - aDate
      })
    }

    return sorted
  }, [filteredTickets, sortBy, sortOrder])

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(sortedTickets.length / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedTickets = useMemo(
    () => sortedTickets.slice(startIndex, endIndex),
    [sortedTickets, startIndex, endIndex]
  )

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedTickets.length / itemsPerPage))
    if (currentPage > maxPage) {
      setCurrentPage(maxPage)
    }
  }, [sortedTickets, currentPage, itemsPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading issues...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">❌ User not found in database!</p>
            <p className="text-red-500 text-xs mb-2">This account was created before user record creation was implemented.</p>
            <p className="text-red-500 text-xs">Please create a new account to test the full flow.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Issues</h1>
            <p className="text-gray-600 mt-1">Manage and track all support issues.</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Filter Dropdown */}
            <FilterDropdown
              filters={filters}
              onFilterChange={setFilters}
              storageKey="manager-issues-filters"
            />

            {/* Date Filter Popover */}
            <DateFilterPopover
              selectedValue={dateFilter}
              onValueChange={setDateFilter}
            />

            {/* Add Issue Button */}
            {hasPermission('CREATE_TICKETS') && (
              <button
                type="button"
                onClick={() => {
                  setCreateError(null)
                  setShowCreateModal(true)
                }}
                className="inline-flex items-center px-4 py-3 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Issue
              </button>
            )}
          </div>
        </div>

        {showSuccessBanner && (
          <TicketSuccessBanner onClose={() => setShowSuccessBanner(false)} />
        )}

        {createError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {createError}
          </div>
        )}

        {/* Issues Table */}
        {hasPermission('VIEW_TICKETS') && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">All Issues</h2>
            </div>

            {ticketsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-3 border-gray-400 border-t-transparent border-r-transparent"></div>
                <span className="ml-2 text-gray-600">Loading issues...</span>
              </div>
            ) : filteredTickets.length > 0 ? (
              <div className="space-y-4">
                {/* Issues Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Issue ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Submitted by
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              onClick={() => handleSort('priority')}
                              className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                            >
                              <span>Priority</span>
                              <ArrowUpDown className={`w-3 h-3 ${sortBy === 'priority' ? 'text-gray-900' : 'text-gray-400'}`} />
                            </button>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Device Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              onClick={() => handleSort('date')}
                              className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                            >
                              <span>Date Submitted</span>
                              <ArrowUpDown className={`w-3 h-3 ${sortBy === 'date' ? 'text-gray-900' : 'text-gray-400'}`} />
                            </button>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedTickets.map((ticket) => {
                          const ticketData = formatTicketForDisplay(ticket)

                          return (
                            <tr key={ticket.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {ticketData.issueId}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticketData.submittedBy}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                {ticketData.description}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  ticketData.priority === 'high'
                                    ? 'bg-red-100 text-red-800'
                                    : ticketData.priority === 'medium'
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {ticketData.priority === 'high' ? 'High' :
                                   ticketData.priority === 'medium' ? 'Medium' : 'Low'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  ticketData.deviceStatus === 'online'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                    ticketData.deviceStatus === 'online' ? 'bg-green-500' : 'bg-gray-400'
                                  }`}></div>
                                  {ticketData.deviceStatus === 'online' ? 'Online' : 'Offline'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticketData.dateSubmitted}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <button
                                  onClick={() => handleIssueClick(ticket.id)}
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {sortedTickets.length > itemsPerPage && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={sortedTickets.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            ) : tickets.length === 0 ? (
              <IssuesEmptyState />
            ) : (
              <div className="rounded-3xl border border-dashed border-gray-200 py-12 text-center text-sm text-gray-500">
                No tickets match the current filters.
              </div>
            )}
          </div>
        )}
      </div>

      <CreateTicketModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          setCreateError(null)
        }}
        onSubmit={handleCreateTicket}
        isSubmitting={createTicketMutation.isPending}
      />
    </DashboardLayout>
  )
}
